// app/src/dashboard/widgets/NewsFeedWidget.tsx
import { useState } from 'react'
import { useQuery } from 'wasp/client/operations'
import { getNewsItems } from 'wasp/client/operations'
import { useTranslations } from '../../client/i18n/useTranslations'
import { Card } from '../../client/components/ui/card'
import { CardSectionHeader } from '../../client/components/ui/card-section-header'

type NewsItem = {
  id: string
  headline: string
  timestamp: string
  url: string
}

/**
 * Nombre d'actualités rendues d'emblée. Le flux amont peut en renvoyer des
 * dizaines : tout rendre allongeait la carte sur mobile et noyait l'info utile.
 * Les suivantes restent accessibles via le scroll interne de la liste.
 */
const INITIAL_NEWS_COUNT = 10

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export function NewsFeedWidget() {
  const t = useTranslations()
  const { data: news, isLoading, error, refetch } = useQuery(getNewsItems)
  const [showAll, setShowAll] = useState(false)

  if (isLoading) {
    return (
      <Card className="h-full">
        <div className="flex flex-col gap-2 p-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="animate-pulse h-8 rounded bg-muted" />
          ))}
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="h-full">
        <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground p-4">
          <p className="text-sm">{t.marketDataWidgets.newsFeed.error}</p>
          <button onClick={() => refetch()} className="text-xs text-primary underline">
            {t.marketDataWidgets.newsFeed.retry}
          </button>
        </div>
      </Card>
    )
  }

  if (!news || news.length === 0) {
    return (
      <Card className="h-full">
        <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
          {t.marketDataWidgets.newsFeed.noNews}
        </div>
      </Card>
    )
  }

  const allNews = news as NewsItem[]
  const visibleNews = showAll ? allNews : allNews.slice(0, INITIAL_NEWS_COUNT)
  const hiddenCount = allNews.length - visibleNews.length

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      <CardSectionHeader
        label={t.marketDataWidgets.newsFeed.title}
        isDark={false}
        compact
        rightContent={<span className="inline-block w-1.5 h-1.5 rounded-full bg-destructive animate-pulse" />}
      />
      <div className="flex-1 overflow-y-auto">
        {visibleNews.map((item) => (
          <a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 px-3 py-2 border-b border-border/40 last:border-0 hover:bg-accent/30 transition-colors group"
          >
            <span className="text-[10px] text-muted-foreground shrink-0 mt-0.5 tabular-nums w-10">
              {formatTime(item.timestamp)}
            </span>
            <p className="text-xs font-medium leading-snug group-hover:text-primary transition-colors">
              {item.headline}
            </p>
          </a>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full px-3 py-2 text-xs font-medium text-primary hover:bg-accent/30 transition-colors"
          >
            {t.marketDataWidgets.newsFeed.showMore.replace('{count}', String(hiddenCount))}
          </button>
        )}
      </div>
    </Card>
  )
}
